/**
 * @file: ReservationPrimaryCta.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The single primary action below the info card that advances a reservation
 * along its happy path: pending → confirm, confirmed → arrive, arrived →
 * create-order.
 */

import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { PrimaryAction } from '@euler/app/flows/reservations/pages/detail/functions/useReservationActions';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  LucideIcon,
} from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity } from 'react-native';

const iconFor = (primary: PrimaryAction): LucideIcon => {
  if (primary.kind === 'createOrder') return ClipboardList;
  return primary.action === 'confirm' ? CheckCircle2 : CalendarCheck;
};

export const ReservationPrimaryCta = memo(
  ({ primary, onPress }: { primary: PrimaryAction; onPress: () => void }) => {
    const { rpx } = useResponsiveContext();
    const Icon = iconFor(primary);
    return (
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={onPress}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          height: rpx(52),
          borderRadius: rpx(14),
          backgroundColor: LD.brand,
          marginBottom: rpx(4),
          shadowColor: LD.brand,
          shadowOpacity: 0.22,
          shadowRadius: rpx(10),
          shadowOffset: { width: 0, height: rpx(3) },
          elevation: 2,
        }}
      >
        <Icon size={rpx(18)} color="#fff" style={{ marginRight: rpx(8) }} />
        <Typography.Text size={rpx(16)} color="#fff" bold>
          {primary.label}
        </Typography.Text>
      </TouchableOpacity>
    );
  },
);
